import {
  type FlowRunExploreColumnsQueryInput,
  type FlowRunExploreColumnsResponse,
  type FlowRunExploreCountQueryInput,
  type FlowRunExploreCountResponse,
  type FlowRunExploreDownloadResponse,
  type FlowRunExploreExportBodyInput,
  type FlowRunExploreExportResponse,
  type FlowRunExploreFindAllResponse,
  type FlowRunExploreQueryInput,
  type FlowRunExportJobCancelResponse,
  type FlowRunExportJobCreateResponse,
  type FlowRunExportJobDownloadResponse,
  type FlowRunExportJobListQueryInput,
  type FlowRunExportJobListResponse,
} from 'flow-schema';

import { type Common } from '../common.js';
import { type FlowRunBlockWithData } from '../models/flow/flowRun.js';
import { type FlowRunExploreRunShell } from '../models/flow/flowRunExplore.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/flow-runs/explore';

  constructor(common: Common) {
    this.common = common;
  }

  // POSTs to `/search` rather than GETting the base path: a large column
  // projection sent as query params overflows the URL/header size limit and 431s.
  async findAll(body: FlowRunExploreQueryInput): Promise<FlowRunExploreFindAllResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/search`);
    return await this.common.postJSON(url, body);
  }

  async getCount(
    queryParams?: FlowRunExploreCountQueryInput
  ): Promise<FlowRunExploreCountResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/count`, queryParams);
    return await this.common.getJSON(url);
  }

  async getColumns(
    queryParams?: FlowRunExploreColumnsQueryInput
  ): Promise<FlowRunExploreColumnsResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/columns`, queryParams);
    return await this.common.getJSON(url);
  }

  async export(body?: FlowRunExploreExportBodyInput): Promise<FlowRunExploreExportResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export`);
    return await this.common.postJSON(url, body);
  }

  async download(body?: FlowRunExploreExportBodyInput): Promise<FlowRunExploreDownloadResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/download`);
    return await this.common.postJSON(url, body);
  }

  async createExportJob(
    body?: FlowRunExploreExportBodyInput
  ): Promise<FlowRunExportJobCreateResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export-jobs`);
    return await this.common.postJSON(url, body);
  }

  async cancelExportJob(jobId: string): Promise<FlowRunExportJobCancelResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export-jobs/${jobId}/cancel`);
    return await this.common.postJSON(url, {});
  }

  async findExportJobs(
    queryParams?: FlowRunExportJobListQueryInput
  ): Promise<FlowRunExportJobListResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export-jobs`, queryParams);
    return await this.common.getJSON(url);
  }

  async getExportJobDownloadUrl(jobId: string): Promise<FlowRunExportJobDownloadResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export-jobs/${jobId}/download`);
    return await this.common.getJSON(url);
  }

  async findOneRunById(flowRunId: string): Promise<{ flowRun: FlowRunExploreRunShell }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/runs/${flowRunId}`);
    return await this.common.getJSON(url);
  }

  async findOneBlockById(flowRunId: string, blockId: string): Promise<FlowRunBlockWithData> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/runs/${flowRunId}/blocks/${blockId}`
    );
    const response: { flowRunBlock: FlowRunBlockWithData } = await this.common.getJSON(url);
    return response.flowRunBlock;
  }
}
